import { Accordion, Stack } from '@chakra-ui/react';
import { useUploadWithdrawBankReceipt } from 'hooks';
import { useTranslation } from 'localization';
import { Transaction } from 'types';

import {
  Button,
  CopyClipBoard,
  DownloadCard,
  LDTransactionCard,
  LDTransactionKeyValue,
  LDTransactionMessage,
} from 'components';

import CompleteLdWithdraw from '../CompleteLdWithdraw';
import UploadWithdrawFile from '../UploadWithdrawFile';

type TransactionCardProps = {
  transaction: Transaction;
  refetch: () => void;
};

const TransactionCard: React.FC<TransactionCardProps> = ({
  transaction,
  refetch,
}: TransactionCardProps) => {
  const { t } = useTranslation('component.requests.transactions');

  const { mutateAsync: approve, isPending: approveLoading } =
    useUploadWithdrawBankReceipt();

  const STATES: Record<Transaction['status'], React.ReactElement | string> = {
    INIT: '',
    AUTHORIZED: '',
    APPROVED: (
      <CompleteLdWithdraw
        refetch={refetch}
        transactionId={transaction?.id || ''}
      />
    ),
    UNAUTHORIZED: (
      <Stack>
        <LDTransactionMessage
          title={t('transactionNotCleared')}
          status="error"
          textProps={{ color: 'error' }}
        />
      </Stack>
    ),
    CLEARED: transaction?.form?.withdraw_ld?.proposal ? (
      transaction?.form?.withdraw_ld?.proposal?.payment_method === 'CASH' ? (
        <Stack flexDir="column">
          <LDTransactionMessage
            title={t('addressHint')}
            status="error"
            textProps={{ color: 'error' }}
          />
          <Button
            title={t('approve')}
            onClick={() =>
              approve({ id: transaction.id }).then(() => refetch())
            }
            isLoading={approveLoading}
          />
        </Stack>
      ) : transaction?.form?.withdraw_ld?.bank_receipt ? (
        <Stack flexDir="column">
          <LDTransactionMessage
            title={t('waitForLDApprove')}
            status="info"
            textProps={{ color: 'info' }}
          />
        </Stack>
      ) : (
        <Stack>
          <LDTransactionKeyValue
            title={t('holderName')}
            value={
              transaction?.form?.withdraw_ld?.proposal?.user_bank_account
                ?.holder_name || ''
            }
            flexDir="column"
          />
          {transaction?.form?.withdraw_ld?.proposal?.user_bank_account
            ?.bank_name && (
            <LDTransactionKeyValue
              title={t('bankName')}
              value={
                transaction?.form?.withdraw_ld?.proposal?.user_bank_account
                  ?.bank_name || ''
              }
            />
          )}
          <LDTransactionKeyValue
            title={t('number')}
            value={
              <CopyClipBoard
                initialValue={
                  transaction?.form?.withdraw_ld?.proposal?.user_bank_account
                    ?.account_id || ''
                }
              />
            }
            flexDir={{ base: 'column', md: 'row' }}
          />
          {transaction?.form?.withdraw_ld?.proposal?.user_bank_account
            ?.IBAN && (
            <LDTransactionKeyValue
              title={t('iban')}
              value={
                <CopyClipBoard
                  initialValue={
                    transaction?.form?.withdraw_ld?.proposal?.user_bank_account
                      ?.IBAN || ''
                  }
                />
              }
              flexDir={{ base: 'column', md: 'row' }}
            />
          )}
          {transaction?.form?.withdraw_ld?.proposal?.user_bank_account
            ?.description && (
            <LDTransactionKeyValue
              title={t('description')}
              value={
                transaction?.form?.withdraw_ld?.proposal?.user_bank_account
                  ?.description || ''
              }
              flexDir="column"
            />
          )}
          <UploadWithdrawFile
            transactionId={transaction?.id || ''}
            refetch={refetch}
          />
        </Stack>
      )
    ) : (
      <Stack>
        <LDTransactionMessage
          title="Cleared"
          status="info"
          textProps={{ color: 'info' }}
        />
      </Stack>
    ),
    NOT_CLEARED: (
      <Stack>
        <LDTransactionMessage
          title={t('transactionNotCleared')}
          status="error"
          textProps={{ color: 'error' }}
        />
      </Stack>
    ),
    CANCELED: (
      <Stack>
        <LDTransactionMessage
          title={t('transactionNotCleared')}
          status="error"
          textProps={{ color: 'error' }}
        />
      </Stack>
    ),
    SETTLED: (
      <Stack>
        {transaction?.form?.withdraw_ld?.proposal
          ? transaction?.form?.withdraw_ld?.proposal?.payment_method ===
              'BANK_TRANSFER' && (
              <DownloadCard
                file={transaction?.form?.withdraw_ld?.bank_receipt || ''}
              />
            )
          : ''}
        <LDTransactionMessage
          title={t('transactionDone')}
          status="success"
          textProps={{ color: 'success' }}
        />
      </Stack>
    ),
    DENIED: (
      <Stack>
        <LDTransactionMessage
          title={t('transactionNotCleared')}
          status="error"
          textProps={{ color: 'error' }}
        />
      </Stack>
    ),
    FAILED: '',
    SETTLING: '',
  };

  return (
    <Accordion.ItemContent pb={4}>
      <Accordion.ItemBody>
        <LDTransactionCard
          amount={transaction.amount}
          identifier_number={
            transaction?.source_wallet?.user?.identifier_number
          }
          pay_with={transaction?.form?.withdraw_ld?.proposal?.pay_with}
          symbol={transaction?.destination_wallet?.currency?.symbol || ''}
          rate={transaction?.form?.withdraw_ld?.proposal?.rate}
          note={transaction?.form?.withdraw_ld?.proposal?.note}
          payment_method={
            transaction?.form?.withdraw_ld?.proposal?.payment_method
          }
          address={transaction?.form?.withdraw_ld?.proposal?.address}
          payingValue={
            transaction.amount * transaction?.form?.withdraw_ld?.proposal?.rate
          }
          panel="LD"
          method="WITHDRAW"
        />
        <Stack mt="2">{STATES[transaction.status]}</Stack>
      </Accordion.ItemBody>
    </Accordion.ItemContent>
  );
};
export default TransactionCard;
